import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class JsonLDService {
  private _document = inject<Document>(DOCUMENT);

  private scriptMap: Map<any, HTMLScriptElement> = new Map();
  private host: HTMLHeadElement = this._document.head;
  private scriptType = 'application/ld+json';

  public websiteSchema = (url?: string, name?: string) => {
    return {
      '@context': 'http://schema.org',
      '@type': 'WebSite',
      url: url || 'https://google.com',
      name: name || 'Google',
      sameAs: ['https://facebook.com/google', 'https://instagram.com/google', 'https://twitter.com/google'],
    };
  };

  public tagLandingPageSchema = (mainEntityUrl, headline: string, imageLinks, created_at = new Date(), updated_at = new Date(), author?: string) => {
    let creator = {
      '@type': 'Organization',
      name: '23degrees',
    };

    if (author) {
      creator = {
        '@type': 'Person',
        name: author,
      };
    }

    return {
      '@context': 'https://schema.org',
      '@type': 'NewsArticle',
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': mainEntityUrl,
      },
      headline: headline,
      image: {
        '@type': 'ImageObject',
        url: imageLinks,
      },
      datePublished: created_at,
      dateModified: updated_at,
      author: creator,
      publisher: {
        '@type': 'Organization',
        name: '23degrees',
        logo: {
          '@type': 'ImageObject',
          url: 'https://app.23degrees.io/assets/android-chrome-512x512.png',
        },
      },
    };
  };

  removeStructuredData(): void {
    // console.log("[JsonLdService] - removeStructuredData");
    const els = [];
    ['structured-data', 'structured-data-org'].forEach((c) => {
      els.push(...Array.from(this._document.head.getElementsByClassName(c)));
    });
    els.forEach((el) => this._document.head.removeChild(el));
  }

  insertSchema(schema, className = 'structured-data'): void {
    try {
      // console.log("[JsonLdService] - insertSchema");
      let script;
      let shouldAppend = false;
      if (this._document.head.getElementsByClassName(className).length) {
        script = this._document.head.getElementsByClassName(className)[0];
      } else {
        script = this._document.createElement('script');
        shouldAppend = true;
      }
      script.setAttribute('class', className);
      script.type = this.scriptType;
      script.text = JSON.stringify(schema);
      if (shouldAppend) {
        this._document.head.insertBefore(script, this._document.head.firstChild);
      }
    } catch (error) {
      console.log('[insertSchema] error', error);
    }
  }

  // New approach
  removeScript(key: any = 'structured-data'): void {
    const scriptEl = this.scriptMap.get(key);
    if (scriptEl) {
      this.scriptMap.delete(key);
      this.host.removeChild(scriptEl);
    }
  }

  addScript(key: any, script: string) {
    const styleEl = this.createScriptNode(script);
    this.scriptMap.set(key, styleEl);
    this.host.insertBefore(styleEl, this.host.firstChild);
  }

  updateScript(schema, className = 'structured-data') {
    const styleEl = this.scriptMap.get(className);
    if (styleEl) {
      styleEl.text = JSON.stringify(schema);
    } else {
      this.addScript(className, JSON.stringify(schema));
    }
  }

  private createScriptNode(content: string): HTMLScriptElement {
    const scriptEl = this._document.createElement('script');
    scriptEl.type = this.scriptType;
    scriptEl.text = content;
    return scriptEl;
  }
}
